import { createContext, useContext, useEffect, useState, useRef } from "react";
import { fetchProducts, getProducts } from "../data/products";

const PRODUCTS_CACHE_KEY = "ekman_products_v3";
const CACHE_VERSION_KEY = "ekman_cache_version";
const CURRENT_VERSION = "2026060201"; // fix stale cache bug

const getCachedProducts = () => {
  try {
    const version = localStorage.getItem(CACHE_VERSION_KEY);
    if (version !== CURRENT_VERSION) {
      localStorage.removeItem(PRODUCTS_CACHE_KEY);
      localStorage.setItem(CACHE_VERSION_KEY, CURRENT_VERSION);
      return null;
    }
    const cached = localStorage.getItem(PRODUCTS_CACHE_KEY);
    if (cached) {
      const parsed = JSON.parse(cached);
      if (parsed && parsed.length > 0) return parsed;
    }
  } catch {}
  return null;
};

const setCachedProducts = (products) => {
  try {
    // Cache only essential fields — keep image (thumbnail base64) for instant display
    const lightProducts = products.map(p => ({
      ...p,
      images: [],  // strip gallery to keep cache small
    }));
    localStorage.setItem(PRODUCTS_CACHE_KEY, JSON.stringify(lightProducts));
  } catch {}
};

const ProductsContext = createContext({
  products: [],
  loading: false,
  refetch: () => {},
});

export function ProductsProvider({ children }) {
  const [products, setProducts] = useState(() => getCachedProducts() || getProducts());
  const [loading, setLoading] = useState(false);
  const hasFetched = useRef(false);

  const refetch = async () => {
    const fetched = await fetchProducts();
    if (fetched && fetched.length > 0) {
      // Merge: never downgrade a working image with an empty value from server
      // (prevents flash-then-disappear when DB is temporarily missing thumbs)
      setProducts(prev => {
        const prevMap = Object.fromEntries(prev.map(p => [p.slug, p]));
        return fetched.map(p => ({
          ...p,
          image: p.image || prevMap[p.slug]?.image || "",
        }));
      });
      setCachedProducts(fetched);
    }
  };

  useEffect(() => {
    // Only fetch ONCE per session, not on every navigation
    if (!hasFetched.current) {
      hasFetched.current = true;
      refetch();
    }
  }, []);

  return (
    <ProductsContext.Provider value={{ products, loading, refetch }}>
      {children}
    </ProductsContext.Provider>
  );
}

export function useProducts() {
  return useContext(ProductsContext);
}
